import { defer, timer } from 'rxjs';
import { repeat, retry, share } from 'rxjs/operators';
import { webSocket, type WebSocketSubject } from 'rxjs/webSocket';

import { DEFAULT_LOBBY_WS_RETRY } from '../constants/socket';

import type {
  CreateLobbyWebSocketConfig,
  LobbySocketMessage,
  LobbyWebSocketHandle,
} from '../types/socket';

export function exponentialBackoffMs(
  attempt: number,
  baseMs: number,
  maxMs: number
): number {
  return Math.min(maxMs, baseMs * 2 ** attempt);
}

/**
 * Creates a lobby WebSocket connection with optional exponential backoff retry/reconnect.
 */
export function createLobbyWebSocket(
  config: CreateLobbyWebSocketConfig
): LobbyWebSocketHandle {
  const resolvedUrl = config.transformUrl
    ? config.transformUrl(config.url)
    : config.url;

  let activeWs: WebSocketSubject<LobbySocketMessage> | null = null;

  const connectSocket = (): WebSocketSubject<LobbySocketMessage> => {
    const ws = webSocket<LobbySocketMessage>({
      url: resolvedUrl,
      openObserver: {
        next: () => {
          ws.next(config.handshake);
        },
      },
    });
    activeWs = ws;
    return ws;
  };

  if (!config.retry) {
    const ws = connectSocket();
    return {
      messages$: ws,
      send: (message) => {
        ws.next(message);
      },
      close: () => {
        ws.complete();
        activeWs = null;
      },
    };
  }

  const {
    maxRetriesOnError = DEFAULT_LOBBY_WS_RETRY.maxRetriesOnError,
    maxReconnectsOnClose = DEFAULT_LOBBY_WS_RETRY.maxReconnectsOnClose,
    baseDelayMs = DEFAULT_LOBBY_WS_RETRY.baseDelayMs,
    maxDelayMs = DEFAULT_LOBBY_WS_RETRY.maxDelayMs,
  } = config.retry;

  const messages$ = defer(() => connectSocket()).pipe(
    retry({
      count: maxRetriesOnError,
      delay: (_err, retryCount) => timer(exponentialBackoffMs(retryCount, baseDelayMs, maxDelayMs)),
      resetOnSuccess: true,
    }),
    repeat({
      count: maxReconnectsOnClose,
      delay: (repeatCount) => timer(exponentialBackoffMs(repeatCount, baseDelayMs, maxDelayMs)),
    }),
    share()
  );

  return {
    messages$,
    send: (message) => {
      activeWs?.next(message);
    },
    close: () => {
      activeWs?.complete();
      activeWs = null;
    },
  };
}
